// client/src/components/bank/BankDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDuration, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { Menu, Play, Star, History, Mail, Filter } from 'lucide-react';

const BankDashboard = () => {
  const { t } = useTranslation();
  const [allRequests, setAllRequests] = useState([]);
  const [stats, setStats] = useState({
    openRequests: 0,
    activeBids: 0,
    totalBiddingValue: 0,
    unbiddedRequests: 0,
    currencyBreakdown: {}
  });
  
  const [newOpportunities, setNewOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAuthenticated, token, initialized } = useAuth();
  
  // Function to calculate stats (no currency filter - shows ALL)
  const calculateStats = (requests, bids) => {
    // Filter unbidded requests (considering only active bids, not cancelled ones)
    const validBids = bids.filter(bid => bid.request !== null);
    const unbiddedRequests = requests.filter(request => {
      const hasActiveBidForRequest = validBids.some(bid => {
        return bid.request?.requestId === request.requestId && bid.status !== 'cancelled';
      });
      return !hasActiveBidForRequest;
    });
    
    // Calculate active bids (ALL)
    const activeBids = validBids.filter(bid => bid.status === 'pending').length;
    
    // Calculate total bidding value (ALL)
    const totalBiddingValue = requests.reduce(
      (sum, request) => sum + request.amount, 
      0
    );
    
    // Calculate currency breakdown for all requests
    const currencyBreakdown = {};
    requests.forEach(request => {
      const curr = request.currency || 'EUR';
      if (!currencyBreakdown[curr]) {
        currencyBreakdown[curr] = {
          totalValue: 0,
          requestCount: 0
        };
      }
      currencyBreakdown[curr].totalValue += request.amount;
      currencyBreakdown[curr].requestCount += 1;
    });
    
    return {
      openRequests: requests.length,
      activeBids,
      totalBiddingValue,
      unbiddedRequests: unbiddedRequests.length,
      currencyBreakdown,
      filteredUnbiddedRequests: unbiddedRequests
    };
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Make sure we don't try to fetch before authentication is initialized
      if (!initialized) {
        return;
      }
      
      // Reset states when starting a new fetch
      setLoading(true);
      setError('');
      
      try {
        // Make sure the token is set in axios headers
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        // Fetch active requests that are open for bidding
        const requestsRes = await axios.get('/api/requests?status=bidding');
        
        // Fetch bank bids
        const bidsRes = await axios.get('/api/bids/bank');
        
        // Store all requests for currency filtering
        setAllRequests(requestsRes.data.requests);
        
        // Calculate initial stats
        const calculatedStats = calculateStats(
          requestsRes.data.requests, 
          bidsRes.data.bids
        );
        
        setStats(calculatedStats);
        
        // Set new opportunities (unbidded requests) - limit to 3
        setNewOpportunities(calculatedStats.filteredUnbiddedRequests.slice(0, 3));
        
        // Filter out bids with null requests for activity
        const validBids = bidsRes.data.bids.filter(bid => bid.request !== null);
  
        setLoading(false);
      } catch (err) {
        console.error('Dashboard data fetch error:');
        setError(t('common.errors.fetchDashboard'));
        setLoading(false);
      }
    };
    
    // Only fetch data if authenticated and initialized
    if (isAuthenticated && initialized) {
      fetchDashboardData();
    }
  }, [isAuthenticated, token, initialized, t]);
  
  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>{t('common.loadingDashboard')}</div>
      </div>
    );
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Reusable button styles with hover effect classes
  const primaryButtonStyle = {
    backgroundColor: '#1a73e8',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '4px',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '14px',
    textAlign: 'center',
    border: 'none',
    minWidth: '120px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const primaryButtonHoverStyle = {
    ...primaryButtonStyle,
    backgroundColor: '#1565d8',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    transform: 'translateY(-1px)'
  };

  const secondaryButtonStyle = {
    backgroundColor: 'white',
    color: '#1a73e8',
    padding: '8px 16px',
    borderRadius: '4px',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '14px',
    textAlign: 'center',
    border: '1px solid #1a73e8',
    minWidth: '120px',
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  };

  const secondaryButtonHoverStyle = {
    ...secondaryButtonStyle,
    backgroundColor: '#e8f0fe',
    borderColor: '#1565d8',
    color: '#1565d8'
  };

  return (
    <div className="bank-dashboard company-dashboard">
      {/* Enhanced Stats grid with card styling */}
      <div className="stats-container" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className="card stat-card" style={{ margin: 0, transition: 'all 0.3s ease' }}>
          <div className="card-title" style={{ margin: '-20px -20px 15px -20px' }}>
            <Menu className="card-title-icon" size={20} />
            <span>{t('dashboard.openRequests')}</span>
          </div>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: '700', color: '#1a73e8', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center',minHeight: '80px'}}>
            {stats.unbiddedRequests}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>
            {t('dashboard.newOpportunities')}
          </div>
        </div>
        
        <div className="card stat-card" style={{ margin: 0, transition: 'all 0.3s ease' }}>
          <div className="card-title" style={{ margin: '-20px -20px 15px -20px' }}>
            <Play className="card-title-icon" size={20} />
            <span>{t('dashboard.activeBids')}</span>
          </div>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: '700', color: '#1a73e8', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
            {stats.activeBids}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>
            {t('dashboard.pendingApproval')}
          </div>
        </div>
        
      </div>
      
      {/* New opportunities card with dividers between items */}
      {newOpportunities.length > 0 && (
        <div className="card">
          <div className="card-title">
            <Star className="card-title-icon" size={20} />
            <span>
              {t('dashboard.newOpportunitiesTitle')}
            </span>
          </div>
          <div style={{ padding: '0' }}>
            {newOpportunities.map((request, index) => (
              <React.Fragment key={request.id}>
                <div className="opportunity-item" style={{ padding: '15px 0' }}>
                  <p style={{ marginBottom: '12px', fontSize: '16px' }}>
                    {t(request.requestType === 'hot_money' ? 'requests.newHotMoneyPosted' : 'requests.newLiquidityPosted', {
                      companyName: request.company?.name,
                      amount: formatCurrencyWithSymbol(request.amount, request.currency || 'EUR'),
                      duration: formatDuration(request.duration, t)
                    })}
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Link 
                      to={`/bank/submit-bid/${request.id}`} 
                      className="btn-primary hover-effect"
                      style={primaryButtonStyle}
                      onMouseOver={(e) => {
                        Object.assign(e.target.style, primaryButtonHoverStyle);
                      }}
                      onMouseOut={(e) => {
                        Object.assign(e.target.style, primaryButtonStyle);
                      }}
                    >
                      {t('bids.submitBid')}
                    </Link>
                    <Link 
                      to={`/bank/open-requests`} 
                      className="btn-secondary hover-effect"
                      style={secondaryButtonStyle}
                      onMouseOver={(e) => {
                        Object.assign(e.target.style, secondaryButtonHoverStyle);
                      }}
                      onMouseOut={(e) => {
                        Object.assign(e.target.style, secondaryButtonStyle);
                      }}
                    >
                      {t('bids.viewAllRequests')}
                    </Link>
                  </div>
                </div>
                {/* Add horizontal divider line between items, but not after the last item */}
                {index < newOpportunities.length - 1 && (
                  <hr style={{ 
                    margin: '0',
                    border: 'none', 
                    height: '1px', 
                    backgroundColor: '#e0e0e0'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      
      
      {/* Support Contact Section */}
      <div className="card" style={{ textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          <Mail size={16} style={{ color: '#6c757d' }} />
          <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>
            {t('dashboard.needHelp', { defaultValue: 'Hai bisogno di aiuto?' })}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
          {t('dashboard.contactSupport', { defaultValue: 'Contatta il nostro supporto:' })}{' '}
          <a href="mailto:support@euracle.it" style={{ color: '#1a73e8', textDecoration: 'none' }}>
            support@euracle.it
          </a>
        </p>
      </div>
    </div>
  );
};

export default BankDashboard;